import Cookies from 'cookies-js';
import { useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useMount } from 'react-use';
import { formatUrl } from 'url-lib';

import { useEventClient } from '../../hooks';

import { Collection } from '../../types';

import {
    saveEvent,
    handleShowAddToCollectionView,
    handleCreateCollection,
    handleAddOrRemoveToCollection,
} from './handlers';

export const EVENT_COOKIE = 'ebEventId';

export const useAffCode = (eventUrl: string, affCode: string) => {
    const formattedUrl = useMemo(
        () =>
            formatUrl(eventUrl, {
                aff: affCode,
            }),
        [eventUrl, affCode],
    );

    return formattedUrl;
};

/**
 * On mount, check if cookie is saved for the given Event
 * and parse.
 *
 * @param handleSave
 * @param eventId
 * @param isAuthenticated
 */
export const useShouldSaveFromLoggedOutRedirect = (
    handleSave: Function,
    eventId: string,
    isAuthenticated: boolean,
) => {
    return useMount(() => {
        const eventIdFromCookie = Cookies.get(EVENT_COOKIE);

        if (
            eventIdFromCookie &&
            eventIdFromCookie === eventId &&
            isAuthenticated
        ) {
            Cookies.expire(EVENT_COOKIE);

            return handleSave({
                eventId,
                isAuthenticated,
                savedByYou: false,
            });
        }
    });
};

/**
 * @param setSaved Stateful callback to manage local state of the eventcard
 * @param onCompleted Callback on successful API call
 *
 * React query mutation hook which updates the event local state as we don;t
 * currently have access to the events via the querycache.
 *
 * Leverages the patterns showcased here with the useMutation hook to
 * handle optimistcally updating the response:
 * https://react-query.tanstack.com/docs/guides/optimistic-updates
 */
export const useSaveEvent = ({
    setSaved,
    onCompleted,
}: {
    setSaved: Function;
    onCompleted: Function;
}) => {
    useEventClient();

    const { mutateAsync: handleSaveEvent } = useMutation(saveEvent, {
        onMutate: ({
            isAuthenticated,
            savedByYou,
        }: {
            isAuthenticated: boolean;
            savedByYou: boolean;
        }) => {
            if (isAuthenticated) {
                setSaved(!savedByYou);
            }

            return { previousState: savedByYou };
        },
        onError: (err: Error, data, context) => {
            setSaved(context?.previousState);
            throw err;
        },
        onSuccess: () => {
            return onCompleted();
        },
    });

    return handleSaveEvent;
};

/**
 *
 * @param userId Current userId
 * @param onCompleted Callback hook on successful mutation
 *
 * Mutation hook which creates a collection and updates
 * the collection and user query cache appropriately on
 * success without needing to make a follow up asynchronous call.
 */
export const useCreateCollection = ({
    userId,
    onCompleted,
}: {
    userId: string;
    onCompleted: Function;
}) => {
    const queryClient = useEventClient();

    const { mutateAsync: createCollection } = useMutation(
        handleCreateCollection,
        {
            onSuccess: (data: Collection, { eventId }) => {
                const userKey = ['userCollections', userId];
                const eventKey = ['eventRenderer__eventCollections', eventId];

                queryClient.setQueryData(eventKey, (old = []) => [
                    data.id,
                    ...(old as []),
                ]);
                queryClient.setQueryData(userKey, (old: any) => {
                    return {
                        ...old,
                        collections: [data, ...old.collections],
                    };
                });
                return onCompleted(data.id, data.name);
            },
        },
    );

    return createCollection;
};

/**
 *
 * @param onSuccess Callback hook on successful mutation
 *
 * Saves the event to the provided collection. Optimistically updates
 * in onMutate and updates the querycache directly onSuccess avoiding the
 * need for a second asynchronous call
 *
 * Highly leverages the optimistic update functionality of useMutation
 * https://react-query.tanstack.com/docs/guides/optimistic-updates
 */
export const useSaveEventToCollection = ({
    onCompleted,
}: {
    onCompleted?: Function;
}) => {
    const queryClient = useEventClient();

    const { mutateAsync: saveEventToCollection } = useMutation(
        handleAddOrRemoveToCollection,
        {
            onMutate: ({
                isChecked,
                collectionId,
                eventId,
            }: {
                isChecked: boolean;
                collectionId: string;
                eventId: string;
            }) => {
                const key = ['eventRenderer__eventCollections', eventId];
                const previousEventCollections = queryClient.getQueryData<
                    string[]
                >(key);

                if (isChecked) {
                    queryClient.setQueryData(key, (old = []) => [
                        ...(old as string[]),
                        collectionId,
                    ]);
                } else {
                    queryClient.setQueryData(key, (old = []) =>
                        (old as string[]).filter(
                            (val: string) => val !== collectionId,
                        ),
                    );
                }

                return { previousState: previousEventCollections };
            },
            onError: (err: Error, data, context) => {
                queryClient.setQueryData(
                    ['eventRenderer__eventCollections', data.eventId],
                    context?.previousState,
                );

                throw err;
            },
            onSuccess: (_, { eventId, collectionId, isChecked }) => {
                return (
                    onCompleted &&
                    onCompleted({ eventId, collectionId, isChecked })
                );
            },
        },
    );

    return saveEventToCollection;
};

/**
 * Hook which fetches the intersection of collections for the
 * current user and the collections the provided event belongs
 * to. Relies on parseEventCollectionResponse to parseEventCollectionResponse
 * in handleShowAddToCollectionView to properly format and parse
 * the data.
 *
 * @param eventId current eventId
 * @param userCollections logged in users collections
 * @param options any additional options to be passed to useQuery
 */
export const useEventCollections = (
    eventId: string,
    userCollections: Collection[],
    options = {},
) => {
    useEventClient();

    const { data: eventCollections, ...attrs } = useQuery(
        ['eventRenderer__eventCollections', eventId],
        async () => {
            const data = await handleShowAddToCollectionView({
                eventId,
                userCollections,
            });

            return data?.collections;
        },
        {
            ...options,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchOnMount: false,
        },
    );

    return { eventCollections, ...attrs };
};
